/**
 * Single source of truth for course-pricing display across the entire app.
 *
 * Rule — matches the backend logic in routes_payments_coupons.py:
 *     base = float(course.discount_price or course.price)
 *
 * i.e. a discount is "active" when `discount_price` is truthy (non-zero) AND
 * strictly LESS than the MRP. Otherwise the full `price` (MRP) is shown.
 *
 * By using these helpers everywhere we guarantee the price shown on:
 *   Landing · Courses listing · Course detail · Navbar · City pages
 *   · Admin · Cart · Checkout · Razorpay modal
 * is ALWAYS derived from the saved admin values, never hard-coded or
 * computed differently on different pages.
 */

/**
 * Resolve the authoritative price fields for a course document returned by
 * `GET /api/courses` or `/api/courses/{id}`.
 *
 * @param {Object} course — course doc from backend
 * @returns {{
 *   mrp: number,            // original/listed price before any discount
 *   final: number,          // what the student actually pays (admin discount only; coupons applied separately)
 *   hasDiscount: boolean,   // true when final < mrp
 *   percentOff: number,     // integer 0-100, 0 when hasDiscount is false
 *   isFree: boolean,        // course is marked free OR final is 0
 * }}
 */
export function getCoursePricing(course) {
  if (!course) {
    return { mrp: 0, final: 0, hasDiscount: false, percentOff: 0, isFree: false };
  }
  const mrp = Number(course.price) || 0;
  const discountRaw = Number(course.discount_price) || 0;
  const hasDiscount = discountRaw > 0 && discountRaw < mrp;
  const final = hasDiscount ? discountRaw : mrp;
  const percentOff = hasDiscount && mrp > 0 ? Math.round((1 - final / mrp) * 100) : 0;
  const isFree = !!course.is_free || final === 0;
  return { mrp, final, hasDiscount, percentOff, isFree };
}

/**
 * Format a numeric INR amount using Indian locale grouping (1,00,000 style).
 * Returns "Free" for 0.
 */
export function formatINR(amount, { showFree = true } = {}) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "₹0";
  if (showFree && n === 0) return "Free";
  return "₹" + n.toLocaleString("en-IN");
}

/**
 * Compute what will actually be charged for an order, accounting for coupon.
 * Mirrors backend coupon application in /payments/create-order.
 */
export function getChargeableAmount(course, coupon = null) {
  const { final } = getCoursePricing(course);
  if (coupon && typeof coupon.final === "number") {
    return Math.max(0, coupon.final);
  }
  return final;
}

/**
 * Resolve the whitelist of payment plans the admin has enabled for this course.
 * "full" is always included (safety fallback). Anything else — e.g. "partial_50"
 * — must be explicitly enabled in Admin → Edit Course → Payment Structure.
 *
 * @param {Object} course
 * @returns {string[]} e.g. ["full"] or ["full", "partial_50"]
 */
export function getAllowedPaymentOptions(course) {
  const raw = Array.isArray(course?.payment_options) ? course.payment_options : null;
  if (!raw || raw.length === 0) return ["full"];
  // De-dupe + guarantee "full" is first and present.
  const uniq = Array.from(new Set(raw.filter((p) => typeof p === "string")));
  const ordered = ["full", ...uniq.filter((p) => p !== "full")];
  return ordered;
}

/**
 * Compute the first-installment amount for a given payment plan.
 * Returns {firstAmount, remainingAmount, installmentsTotal}.
 */
export function computeInstallment(course, coupon = null, plan = "full") {
  const chargeable = getChargeableAmount(course, coupon);
  if (plan === "partial_50") {
    const first = Math.round(chargeable * 0.5);
    return { firstAmount: first, remainingAmount: chargeable - first, installmentsTotal: 2 };
  }
  if (plan === "installment_3") {
    const first = Math.round(chargeable / 3);
    return { firstAmount: first, remainingAmount: chargeable - first, installmentsTotal: 3 };
  }
  return { firstAmount: chargeable, remainingAmount: 0, installmentsTotal: 1 };
}

/**
 * Human-readable label for a payment plan code.
 */
export function paymentPlanLabel(plan) {
  if (plan === "partial_50") return "50% now · 50% later";
  if (plan === "installment_3") return "3 monthly EMIs";
  return "Full payment";
}
